import classNames from 'helpers/class-names'
import { FC, ReactElement, useEffect, useState } from 'react'
import {
  FloatingPortal,
  Side,
  autoUpdate,
  flip,
  offset as floatingOffset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react'

interface Props {
  children: ReactElement
  tip: ReactElement | string
  placement?: Side
  offset?: number
}

export const Tooltip: FC<Props> = ({
  children,
  tip,
  placement: selectedPlacement = 'top',
  offset = 10,
}) => {
  const [visible, setVisible] = useState<boolean>(false)
  const { refs, floatingStyles, context, placement, update } = useFloating({
    open: visible,
    onOpenChange: setVisible,
    placement: selectedPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [floatingOffset(offset), flip({ crossAxis: false }), shift()],
  })

  // Manually update the floating element's position on visibility change.
  // This helps avoid the floating element rendering relative to the page.
  useEffect(() => {
    if (visible) {
      update()
    }
  }, [visible])

  const hover = useHover(context, { move: false })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  const classes = classNames(
    'bg-gray-800/90 text-white text-xs rounded-md break-words font-body',
    'px-3 py-1 -mb-1 transition duration-300 ease-in-out max-w-72',
    {
      block: visible,
      hidden: !visible,
      'origin-bottom': placement === 'top',
      'origin-top -mt-1 mb-0': placement === 'bottom',
      'origin-left': placement === 'right',
      'origin-right': placement === 'left',
    },
  )

  if (!tip) {
    return <span>{children}</span>
  }

  return (
    <>
      <span
        data-testid="tooltip-trigger"
        ref={refs.setReference}
        {...getReferenceProps()}>
        {children}
      </span>
      <FloatingPortal>
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={classNames('z-50', { 'pointer-events-none': !visible })}
          {...getFloatingProps()}>
          <div className={classes}>{tip}</div>
        </div>
      </FloatingPortal>
    </>
  )
}
